"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface DemoCalloutProps {
  text: string;
  visible: boolean;
  placement?: "above" | "below";
}

/** Tooltip com efeito typewriter — texto "digitado" caractere a caractere */
export function DemoCallout({ text, visible, placement = "above" }: DemoCalloutProps) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!visible) {
      setDisplayed("");
      setDone(false);
      return;
    }

    setDisplayed("");
    setDone(false);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        setDone(true);
        clearInterval(id);
      }
    }, 30);

    return () => clearInterval(id);
  }, [text, visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="callout"
          initial={{ opacity: 0, scale: 0.75, y: placement === "above" ? 10 : -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.75, y: placement === "above" ? 10 : -10 }}
          transition={{ type: "spring", stiffness: 340, damping: 24 }}
          className="relative pointer-events-none"
        >
          <div className="relative max-w-[280px] bg-secondary text-secondary-foreground text-xs font-semibold px-4 py-2.5 rounded-2xl shadow-xl ring-1 ring-black/10 text-center leading-snug whitespace-normal">
            <span>{displayed}</span>
            {!done && (
              <span
                className="inline-block w-[2px] h-[0.75em] bg-secondary-foreground ml-[1px] align-middle"
                style={{ animation: "callout-blink 0.7s step-end infinite" }}
              />
            )}

            <span
              className={cn(
                "absolute left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 rounded-sm bg-secondary",
                placement === "above" ? "-bottom-1.5" : "-top-1.5",
              )}
            />
          </div>

          <style>{`
            @keyframes callout-blink {
              0%, 100% { opacity: 1; }
              50%       { opacity: 0; }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function DemoPulse({ visible }: { visible: boolean }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.span
          key="pulse"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          className="absolute top-2 right-2 z-[100] pointer-events-none"
        >
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-secondary" />
          </span>
        </motion.span>
      )}
    </AnimatePresence>
  );
}
